/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, Input } from '@angular/core';
import { Bean, GridOptions, IRowNodeStage, RowNode, StageExecuteParams } from 'ag-grid-community';
import { isEmpty, isNil } from 'lodash';
import { GRID_FEATURE, GridFeature, GridFeatureInitParams, GridFeatureReadyParams } from '../feature.interfaces';

@Bean('groupStage')
@Directive({
  selector: '[ghGridTreeData]',
  providers: [{
    provide: GRID_FEATURE,
    useExisting: GridTreeDataDirective,
    multi: true,
  }],
})
export class GridTreeDataDirective implements GridFeature, IRowNodeStage {
  seedBeans = {
    'groupStage': this,
  };

  @Input('ghGridTreeData') groupBy: string;
  @Input('ghGridTreeDataHasChildren') hasChildren: string;
  @Input('ghGridTreeDataExpanded') expanded: string;

  private getRowNodeId: TransformFn<any, any>;

  initGrid(params: GridFeatureInitParams): void {
    // nothing to do
  }

  enhanceGridOptions(options: GridOptions): void {
    const { groupBy } = this;
    const { getRowNodeId } = options;

    if (isNil(getRowNodeId)) {
      throw new Error(`GridTreeDataDirective: getRowNodeId should be defined for tree data`);
    }

    if (isEmpty(groupBy)) {
      throw new Error(`GridTreeDataDirective: groupBy should be defined for tree data`);
    }

    this.getRowNodeId = getRowNodeId;
  }

  onGridReady(params: GridFeatureReadyParams): void {
    // nothing to do
  }

  onDestroy(): void {
    // nothing to do
  }

  execute(params: StageExecuteParams): any {
    const { rowNode } = params;

    // if (rowNodeTransaction) {
    //   this.groupByRowNodeTransaction(rowNodeTransaction, rowNodeOrder);
    // } else {
    this.groupByRowNode(rowNode);
    // }

    return;
  }

  private groupByRowNode(rowNode: RowNode): void {
    const roots: RowNode[] = [];
    const rowNodeIndex: Hash<RowNode> = {};
    const parentToChildren: Hash<RowNode[]> = {};

    rowNode.allLeafChildren.forEach((node) => {
      const groupKey = this.getNodeGroupKey(node);

      if (this.hasChildren) {
        node.group = this.hasNodeChildren(node);
      }
      if (this.expanded) {
        node.expanded = this.isNodeOpened(node);
      }
      node.childrenAfterGroup = [];

      if (groupKey) {
        let children = parentToChildren[groupKey];
        if (!children) {
          parentToChildren[groupKey] = children = [];
        }
        children.push(node);
      } else {
        roots.push(node);
      }

      rowNodeIndex[this.getRowNodeId(node.data)] = node;
    });

    rowNode.childrenAfterGroup = roots;

    Object.keys(parentToChildren).forEach((parentId) => {
      rowNodeIndex[parentId].childrenAfterGroup = parentToChildren[parentId] || [];
    });
  }

  private getNodeGroupKey(node: RowNode): any {
    return node.data[this.groupBy];
  }

  private hasNodeChildren(node: RowNode): boolean {
    return node.data[this.hasChildren];
  }

  private isNodeOpened(node: RowNode): boolean {
    return node.data[this.expanded];
  }
}
